import { i18nNsFactory } from '@atlas-frontend/i18n';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { getValidPeriodDisplayValueWithFormValueAttached } from '../../common/form';
import { Table } from '../../common/tanstacktable';
import { FilterOption } from '../../common/tanstacktable/renderers/WithFilterOptions';
import { sortStringLower } from '../../common/utils';
import { useCategoryTypeOptions } from './useCategoryTypeOptions';
import translations from './CategoryMappingDetailTable.translations.json';
import { FC, memo, useMemo } from 'react';
import { CategoryMappingType } from '../types';
import styled from 'styled-components';
import { Flex } from 'antd';

const { addTranslations, useTranslation } = i18nNsFactory('category-table');
addTranslations(translations);

const columnHelper = createColumnHelper<CategoryMappingType>();
const EMPTY_DATA: CategoryMappingType[] = [];

export interface CategoryDetailTableProps {
  categoryMappings: CategoryMappingType[];
}

export const CategoryMappingDetailTable = ({
  categoryMappings,
}: CategoryDetailTableProps) => {
  const { t } = useTranslation();
  const typeOptions = useCategoryTypeOptions();

  const cols = useMemo(
    () => [
      columnHelper.accessor('codeNameFrom', {
        header: t('column.codeNameFrom.label'),
        filterFn: 'includesOption',
        meta: {
          width: '30%',
          minWidth: 150,
        },
      }),
      columnHelper.accessor('categoryFrom.categoryType', {
        header: t('column.categoryTypeFrom.label'),
        cell: CategoryTypeCell,
        filterFn: 'includesOption',
        meta: {
          width: 110,
        },
      }),
      columnHelper.accessor('codeNameTo', {
        header: t('column.codeNameTo.label'),
        filterFn: 'includesOption',
        meta: {
          width: '30%',
          minWidth: 150,
        },
      }),
      columnHelper.accessor('categoryTo.categoryType', {
        id: 'categoryTypeTo',
        header: t('column.categoryTypeTo.label'),
        cell: CategoryTypeCell,
        filterFn: 'includesOption',
        meta: {
          width: 110,
        },
      }),
      columnHelper.accessor('description', {
        header: t('column.description.label'),
        meta: {
          width: '40%',
          minWidth: 150,
        },
        sortingFn: 'localeCompareCaseSensitive',
      }),
      columnHelper.accessor(
        (rowData) =>
          rowData.validPeriod &&
          getValidPeriodDisplayValueWithFormValueAttached(rowData.validPeriod),
        {
          id: 'validPeriod',
          header: t('column.validPeriod.label'),
          filterFn: 'validPeriod',
          meta: {
            width: 200,
          },
        }
      ),
    ],
    [t]
  );

  const options = useMemo(
    () => ({
      categoryIdFrom: mapCategoryOptions(categoryMappings, 'categoryIdFrom'),
      categoryTypeFrom: typeOptions,
      categoryIdTo: mapCategoryOptions(categoryMappings, 'categoryIdTo'),
      categoryTypeTo: typeOptions,
    }),
    [categoryMappings, typeOptions]
  );

  return (
    <TableWrapper vertical align="end" gap="middle">
      <Table
        name="category-mapping-detail-table"
        columns={cols}
        data={categoryMappings ?? EMPTY_DATA}
        options={options}
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
      />
    </TableWrapper>
  );
};

const CategoryTypeCell: FC<CellContext<any, any>> = memo((props) => {
  const { t } = useTranslation();
  return t('categoryType.' + props.renderValue());
});

function mapCategoryOptions(
  mappings: CategoryMappingType[] | undefined,
  prop: 'categoryIdFrom' | 'categoryIdTo'
) {
  const uniqueIds = new Set<number>();
  const options: FilterOption[] = [];

  mappings?.forEach((mapping) => {
    const categoryId = mapping[prop];
    if (!uniqueIds.has(categoryId)) {
      options.push({
        id: categoryId,
        name:
          prop === 'categoryIdFrom' ? mapping.codeNameFrom : mapping.codeNameTo,
      });
      uniqueIds.add(categoryId);
    }
  });
  options.sort((a, b) => sortStringLower(a.name, b.name));

  return options;
}

export const TableWrapper = styled(Flex)`
  margin: auto;
`;
